import type { TextMessagingProvider } from '@kizen/api/text-messaging-provider';
import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  createTextMessagingProvider,
  deleteTextMessagingProvider,
} from '@kizen/api/text-messaging-provider';
import { Button } from '@kizen/kds/Button';
import css from '@emotion/css';
import styled from '@emotion/styled';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KizenTypography } from '__app/typography';
import Notice from 'components/Kizen/Notice';
import CardToolbar from 'components/Layout/CardToolbar';
import SmallTableCard from 'components/Layout/SmallTableCard';
import PhoneNumberInput from '__components/Inputs/TextInput/presets/PhoneNumber';
import TextInput from '__components/Inputs/TextInput';
import MaskedTextInput from '__components/Inputs/TextInput/Masked';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useToast } from '__components/ToastProvider';
import { textMessagingProvidersQuery } from 'query/text-messaging-provider';
import AxiosService from 'services/AxiosService';
import { toastVariant } from '__components/ToastProvider';

type SMSSetupField = 'accountSid' | 'authToken' | 'phoneNumber';

type Validate = {
  message: string;
  showMessage: boolean;
};

type SMSSetupProps = {
  showDeleteButton: boolean;
};

type SMSSetupState = {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  showConfirmationModal: boolean;
  showEditModal: boolean;
  errors: Map<SMSSetupField, Validate>;
};

type SMSSetupOperations = {
  closeConfirmationModal(): void;
  closeEditModal(): void;
  deleteSMSInformation(): void;
  openConfirmationModal(): void;
  openEditModal(): void;
  resetState(): void;
  saveSMSInformation(info: Pick<SMSSetupState, SMSSetupField>): void;
  updateAuthToken(authToken: string): void;
  updateAccountSid(accountSid: string): void;
  updatePhoneNumber(phoneNumber: string): void;
};

interface SMSSetupContext extends SMSSetupState {
  ops: SMSSetupOperations;
  providerQuery: UseQueryResult<TextMessagingProvider | null>;
}

const InitialState: SMSSetupState = {
  accountSid: '',
  authToken: '',
  phoneNumber: '',
  showConfirmationModal: false,
  showEditModal: false,
  errors: new Map(),
};

const ctx = createContext<SMSSetupContext>({
  ...InitialState,
  providerQuery: {
    data: null,
    isLoading: false,
  } as SMSSetupContext['providerQuery'],
  ops: {
    closeConfirmationModal: () => null,
    closeEditModal: () => null,
    deleteSMSInformation: () => null,
    openConfirmationModal: () => null,
    openEditModal: () => null,
    resetState: () => null,
    saveSMSInformation: () => null,
    updateAuthToken: () => null,
    updateAccountSid: () => null,
    updatePhoneNumber: () => null,
  },
});

const SMSSetupContext = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showToast] = useToast();

  const [state, setState] = useState<SMSSetupState>(InitialState);
  const errorTimeout = useRef<NodeJS.Timeout | null>(null);

  const providerQueryOptions = useMemo(() => {
    return textMessagingProvidersQuery({
      select: (data) => {
        return data[0] ?? null;
      },
    });
  }, []);
  const { queryKey } = providerQueryOptions;
  const providerQuery = useQuery(providerQueryOptions);

  const createMuation = useMutation({
    mutationFn: (data: Pick<SMSSetupState, SMSSetupField>) => {
      return createTextMessagingProvider(AxiosService, {
        account_sid: data.accountSid,
        auth_token: data.authToken,
        phone_number: data.phoneNumber,
      });
    },
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.setQueryData(queryKey, [result.data]);
      } else {
        queryClient.setQueryData(queryKey, [result.error.provider]);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteTextMessagingProvider(AxiosService, id);
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKey, []);
    },
  });

  const ops = useMemo(() => {
    const openEditModal = () => {
      setState((prev) => {
        return { ...prev, showEditModal: true };
      });
    };

    const closeEditModal = () => {
      if (errorTimeout.current) {
        clearTimeout(errorTimeout.current);
      }

      setState((prev) => {
        return {
          ...prev,
          showEditModal: false,
          errors: new Map(),
          errorTimeout: null,
        };
      });
    };

    const openConfirmationModal = () => {
      setState((prev) => {
        return { ...prev, showConfirmationModal: true };
      });
    };

    const closeConfirmationModal = () => {
      setState((prev) => {
        return { ...prev, showConfirmationModal: false };
      });
    };

    const deleteSMSInformation = async () => {
      if (providerQuery.data) {
        try {
          await deleteMutation.mutateAsync(providerQuery.data.id);

          showToast({
            message: t('Your Twilio account has been removed.'),
          });

          setState(InitialState);
        } catch (error) {
          showToast({
            message: t(
              'Your Twilio account was not removed. Please contact support if the problem perists.'
            ),
            variant: toastVariant.FAILURE,
          });
        }
      }
    };

    const resetState = () => {
      setState(InitialState);
    };

    const saveSMSInformation = async (
      data: Pick<SMSSetupState, SMSSetupField>
    ) => {
      const errors: SMSSetupState['errors'] = new Map();

      if (!data.accountSid) {
        errors.set('accountSid', {
          message: t('This field is required'),
          showMessage: true,
        });
      }

      if (!data.phoneNumber) {
        errors.set('phoneNumber', {
          message: t('This field is required'),
          showMessage: true,
        });
      }

      if (!data.authToken) {
        errors.set('authToken', {
          message: t('This field is required'),
          showMessage: true,
        });
      }

      if (errors.size) {
        setState((prev) => {
          return { ...prev, errors };
        });

        return;
      }

      try {
        const result = await createMuation.mutateAsync(data);

        if (result.ok) {
          showToast({
            message: t('Your Twilio account has been added.'),
          });
        } else {
          showToast({
            message: t(
              "Twilio did not accept your account information. You'll likely want to reconfigure your Twilio account."
            ),
            variant: toastVariant.ALERT,
          });
        }

        setState((prev) => {
          return {
            ...prev,
            showConfirmationModal: false,
            showEditModal: false,
            errors,
            errorTimeout: null,
          };
        });
      } catch (error) {
        showToast({
          message: t(
            'There was an issue adding your Twilio account. Please contact support if the problem perists.'
          ),
          variant: toastVariant.FAILURE,
        });
      }
    };

    const updateAuthToken = (authToken: string) => {
      setState((prev) => {
        prev.errors.delete('authToken');
        return { ...prev, authToken };
      });
    };

    const updateAccountSid = (accountSid: string) => {
      setState((prev) => {
        prev.errors.delete('accountSid');
        return { ...prev, accountSid };
      });
    };

    const updatePhoneNumber = (phoneNumber: string) => {
      setState((prev) => {
        prev.errors.delete('phoneNumber');
        return { ...prev, phoneNumber };
      });
    };

    return {
      closeConfirmationModal,
      closeEditModal,
      deleteSMSInformation,
      openConfirmationModal,
      openEditModal,
      resetState,
      saveSMSInformation,
      updateAuthToken,
      updateAccountSid,
      updatePhoneNumber,
    };
  }, [providerQuery.data, createMuation, deleteMutation, showToast, t]);

  useEffect(() => {
    if (state.errors.size) {
      if (errorTimeout.current) {
        clearTimeout(errorTimeout.current);
      }

      errorTimeout.current = setTimeout(() => {
        setState((prev) => {
          for (const [field, { message }] of prev.errors) {
            prev.errors.set(field, { message, showMessage: false });
          }

          return { ...prev, errorTimeout: null };
        });
      }, 3000);
    }
  }, [state.errors]);

  return (
    <ctx.Provider value={{ ...state, ops, providerQuery }}>
      {children}
    </ctx.Provider>
  );
};

const StyledBasicModal = styled(BasicModal)`
  .modal-body {
    overflow: unset;
  }
`;

const HelpTextWrapper = styled(Notice)`
  margin: 20px 20px 0 20px;

  p > span,
  p > a {
    font-size: inherit;
  }
`;

const InputsContainer = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${({ margin = '20px '}) => css`
    margin: ${margin};
  `}
`;

const SMSSetupView = (props: SMSSetupProps) => {
  const { showDeleteButton } = props;

  const { t } = useTranslation();

  const {
    authToken,
    errors,
    ops,
    phoneNumber,
    providerQuery,
    showConfirmationModal,
    showEditModal,
    accountSid,
  } = useContext(ctx);

  const { data, isLoading } = providerQuery;

  let hasEdits = !!authToken || !!phoneNumber || !!accountSid;

  if (data) {
    hasEdits =
      authToken !== data.auth_token ||
      phoneNumber !== data.phone_number ||
      accountSid !== data.account_sid;
  }

  return (
    <>
      <SmallTableCard>
        <CardToolbar>
          <KizenTypography type="subheader">
            {t('Send SMS from Your Account')}
          </KizenTypography>
        </CardToolbar>
        {!isLoading && data && (
          <InputsContainer>
            <TextInput
              disabled
              label={t('Twilio Account SID')}
              value={data.account_sid}
            />
            <PhoneNumberInput
              disabled
              label={t('My Phone Number')}
              value={data.phone_number}
            />
            {showDeleteButton && (
              <Button
                outline
                color="warning"
                onClick={ops.openConfirmationModal}
              >
                {t('Delete Account')}
              </Button>
            )}
          </InputsContainer>
        )}
        {!isLoading && !data && (
          <>
            <HelpTextWrapper>
              <KizenTypography>
                <KizenTypography as="span">
                  {t(
                    "You'll need your Twilio account SID, Auth Token, and Phone Number to get started. Click "
                  )}
                </KizenTypography>
                <KizenTypography
                  type="anchor"
                  href="https://www.twilio.com/docs/messaging"
                  target="_blank"
                >
                  {t('here')}
                </KizenTypography>
                <KizenTypography as="span">
                  {t(' for more information.')}
                </KizenTypography>
              </KizenTypography>
            </HelpTextWrapper>
            <InputsContainer>
              <Button outline onClick={ops.openEditModal}>
                {t('Add Twilio Account')}
              </Button>
            </InputsContainer>
          </>
        )}
      </SmallTableCard>
      {showConfirmationModal && !showEditModal && (
        <BasicModal
          show
          typeOfContent="text"
          buttonText={t('Confirm Delete')}
          heading={t('Please Confirm Deletion')}
          onHide={ops.closeConfirmationModal}
          onConfirm={ops.deleteSMSInformation}
        >
          <KizenTypography>
            {t(
              'This will remove your Twilio account SID, phone number, and auth token.'
            )}
          </KizenTypography>
        </BasicModal>
      )}
      {showConfirmationModal && showEditModal && (
        <BasicModal
          show
          actionBtnColor="red"
          typeOfContent="text"
          buttonText={t('Discard Changes')}
          heading={t('You Have Unsaved Changes')}
          onHide={ops.closeConfirmationModal}
          onConfirm={() => {
            ops.resetState();
            ops.closeConfirmationModal();
            ops.closeEditModal();
          }}
        >
          <KizenTypography>
            {t('Unsaved changes will be lost, would you like to continue?')}
          </KizenTypography>
        </BasicModal>
      )}
      {showEditModal && (
        <StyledBasicModal
          show
          heading={t('Setup Twilio Account')}
          onConfirm={() =>
            ops.saveSMSInformation({ accountSid, authToken, phoneNumber })
          }
          onHide={() => {
            if (hasEdits) {
              ops.openConfirmationModal();
            } else {
              ops.closeEditModal();
            }
          }}
        >
          <InputsContainer margin="0">
            <TextInput
              inModal
              label={t('Twilio Account SID')}
              placeholder={t('Twilio Account SID')}
              value={accountSid}
              validate={errors.get('accountSid')}
              onChange={ops.updateAccountSid}
            />
            <MaskedTextInput
              displayMaskedValue
              label={t('Auth Token')}
              placeholder={t('Auth Token')}
              value={authToken}
              validate={errors.get('authToken')}
              onChange={ops.updateAuthToken}
            />
            <PhoneNumberInput
              inModal
              label={t('My Phone Number')}
              value={phoneNumber}
              validate={errors.get('phoneNumber')}
              onChange={ops.updatePhoneNumber}
            />
          </InputsContainer>
        </StyledBasicModal>
      )}
    </>
  );
};

export const SMSSetup = (props: SMSSetupProps) => {
  return (
    <SMSSetupContext>
      <SMSSetupView {...props} />
    </SMSSetupContext>
  );
};
